// frontend/src/context/FetchContext.js
import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

// Create context
const FetchContext = createContext();

/**
 * Provider component for tracking API fetch states
 */
export const FetchProvider = ({ children }) => {
  // Track fetching state for different data types
  const [fetchState, setFetchState] = useState({
    plaidData: false,
    calendarStatus: false,
    purchases: false,
    goals: false,
    forecast: false,
    badges: false,
    taxes: false,
    benchmarks: false
  });

  // Use ref to track in-flight requests and prevent duplicates
  const inFlightRequestsRef = useRef(new Set());
  
  // Use ref to store fetch state to avoid stale closures in callbacks
  const fetchStateRef = useRef(fetchState);
  
  // Update ref whenever state changes
  React.useEffect(() => {
    fetchStateRef.current = fetchState;
  }, [fetchState]);

  // Update a specific fetch state
  const updateFetchState = useCallback((dataType, value) => {
    // Check if state is already set to this value to prevent unnecessary updates
    if (fetchStateRef.current[dataType] === value) return;
    
    setFetchState(prev => ({
      ...prev,
      [dataType]: value
    }));
  }, []);

  // Check if a specific data type has been fetched
  const hasFetched = useCallback((dataType) => {
    // Use ref instead of state for most up-to-date value
    return fetchStateRef.current[dataType] === true;
  }, []);

  // Add a new API request to the in-flight set
  const addInFlightRequest = useCallback((requestKey) => {
    if (inFlightRequestsRef.current.has(requestKey)) {
      return false;
    }
    inFlightRequestsRef.current.add(requestKey);
    return true;
  }, []);

  // Remove an API request from the in-flight set
  const removeInFlightRequest = useCallback((requestKey) => {
    inFlightRequestsRef.current.delete(requestKey);
  }, []);

  // Check if an API request is currently in flight
  const isRequestInFlight = useCallback((requestKey) => {
    return inFlightRequestsRef.current.has(requestKey);
  }, []);

  // Reset all fetch states (useful for logout)
  const resetFetchStates = useCallback(() => {
    const initialState = {
      plaidData: false,
      calendarStatus: false,
      purchases: false,
      goals: false,
      forecast: false,
      badges: false,
      taxes: false,
      benchmarks: false
    };
    
    setFetchState(initialState);
    fetchStateRef.current = initialState;
    
    // Clear in-flight requests
    inFlightRequestsRef.current.clear();
  }, []);

  return (
    <FetchContext.Provider
      value={{
        fetchState,
        updateFetchState,
        hasFetched,
        addInFlightRequest,
        removeInFlightRequest,
        isRequestInFlight,
        resetFetchStates
      }}
    >
      {children}
    </FetchContext.Provider>
  );
};

// Custom hook for using the fetch context
export const useFetchContext = () => {
  const context = useContext(FetchContext);
  
  if (!context) {
    throw new Error('useFetchContext must be used within a FetchProvider');
  }
  
  return context;
};

export default FetchContext;